import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Auction } from '@sorare/core/src/atoms/icons/Auction';
import { InstantBuy } from '@sorare/core/src/atoms/icons/InstantBuy';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelL } from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_MARKET_SHOP_INSTANT_BUY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary, wheel } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { ALL_WHEEL_TICKETS } from '@sorare/core/src/lib/wheelCurrencies';

import type {
  HowToWinTicketDialogQuery,
  HowToWinTicketDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const Content = styled(Vertical).attrs({
  gap: 5,
})`
  padding: var(--triple-unit);
`;

const Bullet = styled(Horizontal).attrs({
  gap: 2,
})`
  > :first-child {
    flex-shrink: 0;
  }
`;

const TICKETS_CURRENCY = ALL_WHEEL_TICKETS;

const QUERY = gql`
  query HowToWinTicketDialogQuery($rarity: Rarity!, $sport: Sport!) {
    market {
      id
      activeWheelConfig(rarity: $rarity, sport: $sport) {
        slug
        ticketsRewardedAfterPurchase {
          id
          amount
          currency
        }
        bidToEarnRewardConfigs {
          bidderRank
          rewards {
            id
            ... on InGameCurrencyRewardConfig {
              id
              amount
              currency
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  HowToWinTicketDialogQuery,
  HowToWinTicketDialogQueryVariables
>;

type Props = {
  open: boolean;
  rarity?: Rarity;
  onClose: () => void;
};

export const HowToWinTicketDialog = ({
  rarity = Rarity.limited,
  open,
  onClose,
}: Props) => {
  const { sport, generateSportContextPath } = useSportContext();

  const { data } = useQuery(QUERY, {
    variables: {
      rarity,
      sport,
    },
    skip: !open,
  });

  const wheelConfig = data?.market?.activeWheelConfig;
  if (!wheelConfig) {
    return null;
  }
  const { ticketsRewardedAfterPurchase, bidToEarnRewardConfigs } = wheelConfig;

  const nbTicketsAfterPurchase = TICKETS_CURRENCY.includesValue(
    ticketsRewardedAfterPurchase?.currency
  )
    ? ticketsRewardedAfterPurchase?.amount
    : 0;

  const ticketsForRank = bidToEarnRewardConfigs.reduce<
    { rank: number; nbTickets: number }[]
  >((acc, bidToEarn) => {
    const inGameCurrencyRewards = bidToEarn.rewards
      .filter(reward => isType(reward, 'InGameCurrencyRewardConfig'))
      .filter(reward => TICKETS_CURRENCY.includesValue(reward.currency));

    if (inGameCurrencyRewards.length > 0) {
      acc.push({
        rank: bidToEarn.bidderRank,
        nbTickets: inGameCurrencyRewards[0].amount,
      });
    }

    return acc;
  }, []);

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      onClose={onClose}
      title={
        <LabelL bold>
          <FormattedMessage {...wheel.howToWin} />
        </LabelL>
      }
    >
      <Content>
        <Vertical gap={2}>
          {nbTicketsAfterPurchase > 0 && (
            <>
              <Bullet>
                <InstantBuy size={3} color="var(--c-white)" />
                <BodyM color="var(--c-nd-600)" as="p">
                  <FormattedMessage
                    defaultMessage="Buy an eligible Card through Instant Buy to win {nbTickets, plural, one {a ticket} other {# tickets}}"
                    description="v3"
                    values={{ nbTickets: nbTicketsAfterPurchase }}
                  />
                </BodyM>
              </Bullet>
              <Bullet>
                <Auction size={3} color="var(--c-white)" />
                <BodyM color="var(--c-nd-600)" as="p">
                  <FormattedMessage
                    defaultMessage="Buy an eligible Card through Auction to win {nbTickets, plural, one {a ticket} other {# tickets}}"
                    description="v3"
                    values={{ nbTickets: nbTicketsAfterPurchase }}
                  />
                </BodyM>
              </Bullet>
            </>
          )}
          {ticketsForRank.map(({ rank, nbTickets }) => (
            <Bullet key={rank}>
              <Auction size={3} color="var(--c-white)" />
              <BodyM color="var(--c-nd-600)" as="p">
                <FormattedMessage
                  defaultMessage="End an eligible Card Auction with the {rank} highest bid to win {nbTickets, plural, one {a ticket} other {# tickets}}"
                  description="v3"
                  values={{
                    rank: <Ordinal ordinal={rank + 1} />,
                    nbTickets,
                  }}
                />
              </BodyM>
            </Bullet>
          ))}
        </Vertical>
        <BodyM color="var(--c-nd-600)" as="p">
          <FormattedMessage defaultMessage="Other ways of winning tickets may be added in the future — stay posted" />
        </BodyM>
        <Vertical>
          <Button
            size="large"
            fullWidth
            color="primary"
            to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY)}
          >
            <FormattedMessage defaultMessage="Shop Cards" />
          </Button>
          <Button size="large" fullWidth color="quaternary" onClick={onClose}>
            <FormattedMessage {...glossary.close} />
          </Button>
        </Vertical>
      </Content>
    </Dialog>
  );
};
